import type { DataTableColumnDef } from '@neo/blocks/cx-data-table'
import type { DataTableFilterField } from '@neo/blocks/data-table/types'
import { Typography } from '@neo/primitives/typography'
import { pluralize } from '@shared/utils'
import type { RUserSession } from 'v2/types/api/auth'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import dayjs from 'v2/utils/dayjs'
import { IntegrationState } from '../utils/constants'
import { AppLogoMark } from './catalog-row'
import type { ExternalAppRow } from './external-app-actions'
import { connections, rollUpConnectedOn } from './external-apps-rollup'
import { ToolActionsMenu, type ToolMenuAction } from './tool-actions-menu'

export interface ToolActionsColumnProps {
  canEdit: boolean
  canDelete: boolean
  onAction: (action: ToolMenuAction, targets: ExternalAppRow[]) => void
  targetsOf: (row: ExternalAppRow) => ExternalAppRow[]
}

export const integrationStatusOptions = [
  { label: 'Active', value: IntegrationState.ACTIVE },
  { label: 'Disabled', value: IntegrationState.DISABLED },
  { label: 'Expired', value: IntegrationState.EXPIRED },
]

export const getColumns = (
  currentUser: RUserSession | undefined,
  toolActions: ToolActionsColumnProps,
): DataTableColumnDef<ExternalAppRow>[] => [
  // Column sizes are proportions, not pixels: the table stretches and shares out slack by ratio.
  {
    accessorKey: 'name',
    header: 'Tool',
    size: 360,
    filterFn: (row, _, filterValue) => {
      const name = row.original.name?.toLowerCase() || ''
      return name.includes(filterValue?.toLowerCase())
    },
    Cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <div className="h-10 w-10 p-2 bg-bg3 rounded-md">
          <AppLogoMark app={row.original} />
        </div>
        <Typography variant="S/500">{row.original.name}</Typography>
      </div>
    ),
  },
  {
    id: 'accounts',
    header: 'Connected accounts',
    // No Status or Owner column: both are per-account facts, and a tool can hold several.
    size: 200,
    Cell: ({ row }) => (
      <Typography variant="S/400" data-testid="text-connected-accounts">
        {pluralize(connections(row.original).length, 'account')}
      </Typography>
    ),
  },
  {
    accessorKey: 'created_on',
    header: 'Created On',
    size: 220,
    Cell: ({ row }) => {
      // Most recent connection, rather than an arbitrary one of the provider's accounts.
      const date = rollUpConnectedOn(row.original)
      if (!date) return null

      return <Typography variant="S/400">{dayjs(date).format('MMM D, YYYY')}</Typography>
    },
  },
  {
    id: 'actions',
    header: '',
    size: 56,
    Cell: ({ row }) => (
      <div className="flex justify-end">
        <ToolActionsMenu
          row={row.original}
          targets={toolActions.targetsOf(row.original)}
          currentUser={currentUser}
          canEdit={toolActions.canEdit}
          canDelete={toolActions.canDelete}
          onAction={toolActions.onAction}
        />
      </div>
    ),
  },
]

export const getFilterFields = (): DataTableFilterField<RExternalOrgApp>[] => [
  {
    label: 'Name',
    value: 'name',
    paramKey: 'connection_details',
    placeholder: 'Search',
  },
  {
    label: 'Status',
    value: 'state',
    paramKey: 'state',
    options: integrationStatusOptions,
  },
]

// Transform flat app data into grouped structure
export const buildGroupedTableRows = (apps: RExternalOrgApp[]): ExternalAppRow[] => {
  // Group apps by provider
  const grouped = apps.reduce(
    (acc, app) => {
      const provider = app.provider || app.name
      if (!acc[provider]) {
        acc[provider] = []
      }
      acc[provider].push(app)
      return acc
    },
    {} as Record<string, RExternalOrgApp[]>,
  )

  // Transform into grouped rows
  return Object.entries(grouped).map(([provider, providerApps]) => {
    const firstApp = providerApps[0]

    const groupRow: ExternalAppRow = {
      ...firstApp, // Include all properties from RExternalOrgApp
      id: `group-${provider}`,
      name: firstApp.name,
      state: '',
      created_on: '',
      provider,
      isGroup: true,
      subRows: providerApps.map((app) => ({
        ...app, // Include all original app properties first
        provider: app.provider || app.name,
        isGroup: false,
      })),
    }

    return groupRow
  })
}

// The status filter narrows the rows shown, never what a tool's menu or detail sheet reaches:
// `groups` holds each tool's unfiltered group row, keyed by id.
export const buildToolsTable = (apps: RExternalOrgApp[], statusFilter: string[]) => {
  const visible =
    statusFilter.length > 0
      ? apps.filter((app) => statusFilter.includes(app.state?.toUpperCase()))
      : apps
  const groups = new Map(buildGroupedTableRows(apps).map((group) => [group.id, group] as const))
  return { rows: buildGroupedTableRows(visible), groups }
}
